import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { obtenerAmbitoAprendizaje, registrarAmbitoAprendizaje, editarAmbitoAprendizaje, eliminarAmbitoAprendizaje, eliminarAmbitoCompleto } from '../services/evaluacionesService';
import { Save, X, PlusCircle, ListChecks, FileCog, Edit2, Trash2, Plus, ChevronDown, ChevronUp, FolderOpen } from 'lucide-react';
import PlantillaEvaluacion from '../components/PlantillaEvaluacion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

Modal.setAppElement('#root');

const useResizeObserver = () => {
  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 });
  const ref = React.useRef(null);

  React.useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        requestAnimationFrame(() => {
          setDimensions({ width, height });
        });
      }
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return [ref, dimensions];
};

const GestionAmbitosAprendizaje = () => {
  const [containerRef, containerDimensions] = useResizeObserver();
  const [ambitos, setAmbitos] = useState([]);
  const [ambitosExistentes, setAmbitosExistentes] = useState([]);
  const [formData, setFormData] = useState({
    grupo_edad: '',
    ambito_aprendizaje: '',
    indicadores: [''],
    nuevo_ambito: ''
  });
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [ambitoSeleccionado, setAmbitoSeleccionado] = useState(null);
  const [ambitoEditando, setAmbitoEditando] = useState(null);
  const [vistaActual, setVistaActual] = useState('ambitos');
  const [gruposEdad, setGruposEdad] = useState([]);
  const [grupoEdadSeleccionado, setGrupoEdadSeleccionado] = useState('');
  const [expandedAmbito, setExpandedAmbito] = useState(null);
  const [ambitosFiltrados, setAmbitosFiltrados] = useState({});
  const [modoPersonalizado, setModoPersonalizado] = useState(false);

  // Función para ordenar grupos de edad de menor a mayor
  const ordenarGruposEdad = (grupos) => {
    const orden = [
      '3-6 meses',
      '6-9 meses',
      '9-12 meses',
      '12-24 meses',
      '24-36 meses',
      '36-48 meses'
    ];
    return grupos.sort((a, b) => orden.indexOf(a) - orden.indexOf(b));
  };

  const cargarAmbitos = async () => {
    setLoading(true);
    try {
      const response = await obtenerAmbitoAprendizaje();
      if (response.success && response.data.length > 0) {
        // Procesar datos para agrupar indicadores por ámbito
        const ambitosAgrupados = {};
        response.data.forEach(item => {
          const key = `${item.grupo_edad}_${item.ambito_aprendizaje}`;
          if (!ambitosAgrupados[key]) {
            ambitosAgrupados[key] = {
              id_amb_aprendizaje: item.id_amb_aprendizaje,
              grupo_edad: item.grupo_edad,
              ambito_aprendizaje: item.ambito_aprendizaje,
              indicadores: []
            };
          }
          ambitosAgrupados[key].indicadores.push(item.indicador);
        });
        
        // Ordenar los ámbitos por grupo de edad
        const ambitosOrdenados = Object.values(ambitosAgrupados).sort((a, b) => {
          return ordenarGruposEdad([a.grupo_edad, b.grupo_edad]).indexOf(a.grupo_edad) - 
                 ordenarGruposEdad([a.grupo_edad, b.grupo_edad]).indexOf(b.grupo_edad);
        });
        setAmbitos(ambitosOrdenados);
        
        // Extraer ámbitos únicos para el select
        const ambitosUnicos = [...new Set(response.data.map(item => item.ambito_aprendizaje))];
        setAmbitosExistentes(ambitosUnicos);
        
        // Obtener grupos de edad y ordenarlos
        const grupos = [...new Set(response.data.map(ambito => ambito.grupo_edad))];
        setGruposEdad(ordenarGruposEdad(grupos));
      }
    } catch (error) {
      showErrorToast('Error al cargar los ámbitos');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const AMBITOS_APRENDIZAJE_OPCIONES = [
    "Vinculación emocional y social",
    "Exploración del cuerpo y motricidad", 
    "Descubrimiento del medio natural y cultural",
    "Lenguaje verbal y no verbal"
  ];

  const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
  };

  const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
  };

  const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
  };
  useEffect(() => {
    cargarAmbitos();
  }, []);

  useEffect(() => {
    if (grupoEdadSeleccionado) {
      const filtrados = ambitos.filter(ambito => ambito.grupo_edad === grupoEdadSeleccionado);
      
      // Agrupar por ámbito de aprendizaje
      const porAmbito = {};
      filtrados.forEach(item => {
        if (!porAmbito[item.ambito_aprendizaje]) {
          porAmbito[item.ambito_aprendizaje] = [];
        }
        porAmbito[item.ambito_aprendizaje].push(item);
      });
      
      setAmbitosFiltrados(porAmbito);
    }
  }, [grupoEdadSeleccionado, ambitos]);

  const manejarCambioIndicador = (index, valor) => {
    const nuevosIndicadores = [...formData.indicadores];
    nuevosIndicadores[index] = valor;
    setFormData(prev => ({ ...prev, indicadores: nuevosIndicadores }));
  };

  const agregarIndicador = () => {
    setFormData(prev => ({
      ...prev,
      indicadores: [...prev.indicadores, '']
    }));
  };

  const eliminarIndicador = (index) => {
    if (formData.indicadores.length > 1) {
      const nuevosIndicadores = formData.indicadores.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, indicadores: nuevosIndicadores }));
    } else {
      showWarningToast('Debe mantener al menos un indicador');
    }
  };

  const resetForm = () => {
    setFormData({ grupo_edad: '', ambito_aprendizaje: '', indicadores: [''], nuevo_ambito: '' });
    setAmbitoSeleccionado(null);
    setAmbitoEditando(null);
    setModoPersonalizado(false);
  };

  // Función para eliminar un indicador individual de un ámbito existente
  const ToastContentEliminarIndicador = ({ onConfirm, onCancel }) => (
    <div>
      <p>¿Está seguro de eliminar este indicador? Esta acción no se puede deshacer.</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '10px' }}>
        <button onClick={onCancel} style={{ background: '#ccc', border: 'none', padding: '6px 12px', borderRadius: '4px' }}>Cancelar</button>
        <button onClick={onConfirm} style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px' }}>Eliminar</button>
      </div>
    </div>
  );

  const eliminarIndicadorIndividual = async (ambito, indicadorIndex) => {
    const nuevosIndicadores = ambito.indicadores.filter((_, index) => index !== indicadorIndex);
    
    if (nuevosIndicadores.length === 0) {
      showWarningToast('No se puede eliminar el último indicador. Elimine todo el ámbito si es necesario.');
      return;
    }

    const toastId = `eliminar-indicador-${ambito.id_amb_aprendizaje}-${indicadorIndex}`;
    toast.warn(
      <ToastContentEliminarIndicador
        onConfirm={async () => {
          toast.dismiss(toastId);
          setLoading(true);
          try {
            // Eliminar el ámbito completo
            await eliminarAmbitoCompleto(ambito.id_amb_aprendizaje);
            // Recrear el ámbito con los indicadores restantes
            const promesasCreacion = nuevosIndicadores.map(indicador => {
              const datosNuevoAmbito = {
                grupo_edad: ambito.grupo_edad,
                ambito_aprendizaje: ambito.ambito_aprendizaje,
                indicador: indicador
              };
              return registrarAmbitoAprendizaje(datosNuevoAmbito);
            });
            await Promise.all(promesasCreacion);
            await cargarAmbitos();
            showSuccessToast('Indicador eliminado exitosamente');
          } catch (error) {
            console.error('Error al eliminar indicador:', error);
            showErrorToast('Error al eliminar el indicador: ' + (error.response?.data?.message || error.message));
          } finally {
            setLoading(false);
          }
        }}
        onCancel={() => toast.dismiss(toastId)}
      />, {
        toastId,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        position: 'top-center',
        style: {
          fontFamily: 'Exo 2, sans-serif',
          fontWeight: 500
        }
      }
    );
  };


  const manejarCambio = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({...prev, [name]: value}));
    
    if (name === 'nuevo_ambito' && modoPersonalizado) {
      setFormData(prev => ({...prev, ambito_aprendizaje: value}));
    }
  };

  const toggleModoPersonalizado = () => {
    setModoPersonalizado(!modoPersonalizado);
    setFormData(prev => ({
      ...prev,
      ambito_aprendizaje: '',
      nuevo_ambito: ''
    }));
  };

  const manejarSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const ambitoFinal = modoPersonalizado ? formData.nuevo_ambito : formData.ambito_aprendizaje;

    if (!formData.grupo_edad || !ambitoFinal || formData.indicadores.some(ind => !ind.trim())) {
      showWarningToast('Todos los campos son requeridos y no debe haber indicadores vacíos.');
      setLoading(false);
      return;
    }

    try {
      if (modalTitle === 'Registrar Ámbito de Aprendizaje') {
        // En modo registro, creamos nuevos registros para cada indicador
        const promesas = formData.indicadores.map(indicador => {
          const datos = {
            grupo_edad: formData.grupo_edad,
            ambito_aprendizaje: ambitoFinal,
            indicador: indicador
          };
          return registrarAmbitoAprendizaje(datos);
        });
        await Promise.all(promesas);
      } else if (ambitoSeleccionado) {
        // Modo edición: eliminar ámbito existente y crear nuevos registros
        // Primero eliminar el ámbito completo
        await eliminarAmbitoCompleto(ambitoSeleccionado.id_amb_aprendizaje);
        
        // Luego crear nuevos registros para cada indicador
        const promesasCreacion = formData.indicadores.map(indicador => {
          const datosNuevoAmbito = {
            grupo_edad: formData.grupo_edad,
            ambito_aprendizaje: ambitoFinal,
            indicador: indicador
          };
          return registrarAmbitoAprendizaje(datosNuevoAmbito);
        });
        
        await Promise.all(promesasCreacion);
      }
      
      await cargarAmbitos();
      showSuccessToast(`Ámbito de aprendizaje ${modalTitle === 'Registrar Ámbito de Aprendizaje' ? 'registrado' : 'actualizado'} exitosamente.`);
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      showErrorToast(error.message || 'Error al procesar la solicitud.');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalRegistro = () => {
    setModalTitle('Registrar Ámbito de Aprendizaje');
    resetForm();
    setIsModalOpen(true);
  };

  const abrirModalEdicion = (ambito) => {
    setModalTitle('Editar Ámbito de Aprendizaje');
    setAmbitoSeleccionado(ambito);
    setAmbitoEditando(ambito);
    setFormData({
      grupo_edad: ambito.grupo_edad,
      ambito_aprendizaje: ambito.ambito_aprendizaje,
      indicadores: ambito.indicadores,
      nuevo_ambito: ''
    });
    setModoPersonalizado(false);
    setIsModalOpen(true);
  };

  const manejarEliminar = async (id) => {
    const result = window.confirm('¿Está seguro de eliminar este ámbito? Esta acción no se puede deshacer.');
    if (result) {
      setLoading(true);
      try {
        const response = await eliminarAmbitoAprendizaje(id);
        if (response.success) {
          await cargarAmbitos();
          showSuccessToast('Ámbito eliminado exitosamente.');
        }
      } catch (error) {
        showErrorToast('Error al eliminar el ámbito: ' + (error.response?.data?.message || error.message));
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleExpandAmbito = (ambitoKey) => {
    setExpandedAmbito(expandedAmbito === ambitoKey ? null : ambitoKey);
  };

  const ToastContentEliminarAmbito = ({ onConfirm, onCancel }) => (
  <div>
    <p>¿Está seguro de eliminar este ámbito y todos sus indicadores? Esta acción eliminará el ámbito completo y todos sus indicadores asociados. Esta acción no se puede deshacer.</p>
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '10px' }}>
      <button onClick={onCancel} style={{ background: '#ccc', border: 'none', padding: '6px 12px', borderRadius: '4px' }}>Cancelar</button>
      <button onClick={onConfirm} style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px' }}>Eliminar</button>
    </div>
  </div>
);

const manejarEliminarAmbitoCompleto = async (ambito) => {
  const toastId = `eliminar-ambito-${ambito.id_amb_aprendizaje}`;
  toast.warn(
    <ToastContentEliminarAmbito
      onConfirm={async () => {
        toast.dismiss(toastId);
        setLoading(true);
        try {
          await eliminarAmbitoCompleto(ambito.id_amb_aprendizaje);
          await cargarAmbitos();
          showSuccessToast('Ámbito y todos sus indicadores eliminados exitosamente.');
        } catch (error) {
          showErrorToast('Error al eliminar el ámbito: ' + (error.response?.data?.message || error.message));
        } finally {
          setLoading(false);
        }
      }}
      onCancel={() => toast.dismiss(toastId)}
    />, {
      toastId,
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      position: 'top-center',
      style: {
        fontFamily: 'Exo 2, sans-serif',
        fontWeight: 500
      }
    }
  );
};

  return (
    <div className="min-h-screen bg-white rounded-2xl p-2 sm:p-4 shadow-lg">
      <div ref={containerRef} className="max-w-7xl mx-auto bg-white rounded-xl">
        <div className="bg-white rounded-t-xl p-2 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <FileCog className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-500" />
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">Gestión de Evaluaciones</h1>
                <p className="text-sm text-gray-500">Sistema de administración de evaluaciones</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {['ambitos', 'plantillas'].map((vista) => (
                <button
                  key={vista}
                  onClick={() => setVistaActual(vista)}
                  className={`px-3 sm:px-4 py-2 rounded-2xl text-xs sm:text-sm font-medium transition-all border border-gray-300 hover:border-gray-400 flex-1 sm:flex-none ${
                    vistaActual === vista
                      ? 'bg-blue-600 text-white border'
                      : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {vista === 'ambitos' ? 'Ámbitos' : 'Plantillas'}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {loading && (
            <div className="text-center text-gray-400 py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-2">Cargando...</p>
            </div>
          )}

          {vistaActual === 'ambitos' && !loading && (
            <div>
              <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-grow">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filtrar por Grupo de Edad
                  </label>
                  <select
                    value={grupoEdadSeleccionado}
                    onChange={(e) => setGrupoEdadSeleccionado(e.target.value)}
                    className="w-full sm:max-w-xs p-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Todos los grupos</option>
                    {gruposEdad.map((grupo) => (
                      <option key={grupo} value={grupo}>
                        {grupo}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={abrirModalRegistro}
                  className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                >
                  <PlusCircle className="w-4 h-4" />
                  <span>Nuevo Ámbito</span>
                </button>
              </div>

              {!grupoEdadSeleccionado && (
                <div className="text-center text-gray-400 py-8 border border-dashed border-gray-300 rounded-lg">
                  <div className="mb-2">
                    <ListChecks className="w-10 h-10 mx-auto text-gray-300" />
                  </div>
                  <p className="font-medium">Seleccione un grupo de edad para filtrar los ámbitos</p>
                  <p className="text-sm mt-1">O agregue un nuevo ámbito usando el botón superior</p>
                </div>
              )}

              {grupoEdadSeleccionado && Object.keys(ambitosFiltrados).length === 0 && (
                <div className="text-center text-gray-400 py-8 border border-dashed border-gray-300 rounded-lg">
                  <div className="mb-2">
                    <ListChecks className="w-10 h-10 mx-auto text-gray-300" />
                  </div>
                  <p className="font-medium">No hay ámbitos registrados para este grupo de edad</p>
                  <p className="text-sm mt-1">Haga clic en "Nuevo Ámbito" para agregar uno</p>
                </div>
              )}

              {grupoEdadSeleccionado && Object.keys(ambitosFiltrados).length > 0 && (
                <div className="space-y-4">
                  {Object.entries(ambitosFiltrados).map(([nombreAmbito, ambitosDeGrupo]) => (
                    <div key={nombreAmbito} className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200">
                      <div 
                        className="bg-gray-50 p-4 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => toggleExpandAmbito(nombreAmbito)}
                      >
                        <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <div className="font-medium text-gray-800">{nombreAmbito}</div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirModalEdicion(ambitosDeGrupo[0]);
                      }}
                      className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors duration-200 rounded-full hover:bg-blue-50"
                      title="Editar el ámbito seleccionado"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        manejarEliminarAmbitoCompleto(ambitosDeGrupo[0]);
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors duration-200 rounded-full hover:bg-red-50"
                      title="Eliminar el ámbito completo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {expandedAmbito === nombreAmbito ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </div>
            </div>
            
            {expandedAmbito === nombreAmbito && (
              <div className="p-4 border-t border-gray-100">
                <div className="space-y-3">
                  {ambitosDeGrupo[0].indicadores.map((indicador, idx) => (
                    <div key={idx} className="flex items-start group">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-xs font-medium">{idx + 1}</span>
                      </div>
                      <p className="text-sm text-gray-700 flex-grow">{indicador}</p>
                      <button
                        onClick={() => eliminarIndicadorIndividual(ambitosDeGrupo[0], idx)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-500 transition-all duration-200 rounded-full hover:bg-red-50"
                        title="Eliminar este indicador"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {vistaActual === 'plantillas' && (
            <div className="space-y-6">
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grupo de Edad
                </label>
                <select
                  value={formData.grupo_edad}
                  onChange={manejarCambio}
                  name="grupo_edad"
                  className="w-full p-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Seleccionar grupo de edad...</option>
                  {gruposEdad.map((grupo) => (
                    <option key={grupo} value={grupo}>
                      {grupo}
                    </option>
                  ))}
                </select>
              </div>
              <PlantillaEvaluacion grupoEdad={formData.grupo_edad} />
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        className="bg-white rounded-xl p-4 sm:p-6 w-[95%] sm:w-full max-w-lg mx-auto mt-20 shadow-xl"
        overlayClassName="fixed inset-0 bg-black/50 flex items-start justify-center"
      >
        <div className="relative">
          <button
            onClick={() => {
              setIsModalOpen(false);
              resetForm();
            }}
            className="absolute top-0 right-0 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          <h2 className="text-lg sm:text-xl font-medium text-gray-800 mb-4 sm:mb-6">{modalTitle}</h2>

          <form onSubmit={manejarSubmit} className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grupo de Edad
                </label>
                <select
                  name="grupo_edad"
                  value={formData.grupo_edad}
                  onChange={manejarCambio}
                  className="w-full p-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Seleccionar...</option>
                  {gruposEdad.map((grupo) => (
                    <option key={grupo} value={grupo}>
                      {grupo}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Ámbito de Aprendizaje
                  </label>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={toggleModoPersonalizado}
                      className="text-blue-500 hover:text-blue-700 text-xs font-medium flex items-center"
                      title="Cambiar entre crear nuevo ámbito o usar uno existente"
                    >
                      {modoPersonalizado ? (
                        <FolderOpen className="w-4 h-4" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                {modoPersonalizado ? (
                  <input
                    type="text"
                    name="nuevo_ambito"
                    value={formData.nuevo_ambito}
                    onChange={manejarCambio}
                    placeholder="Escriba el nuevo ámbito..."
                    className="w-full p-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                ) : (
                  <select
                    name="ambito_aprendizaje"
                    value={formData.ambito_aprendizaje}
                    onChange={manejarCambio}
                    className="w-full p-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Seleccionar...</option>
                    {ambitosExistentes.map((ambito) => (
                      <option key={ambito} value={ambito}>
                        {ambito}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Indicadores
                </label>
                <button
                  type="button"
                  onClick={agregarIndicador}
                  className="text-blue-500 hover:text-blue-700 text-sm font-medium flex items-center"
                  title="Agregar un nuevo indicador"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                {formData.indicadores.map((indicador, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mt-2">
                      <span className="text-xs font-medium">{index + 1}</span>
                    </div>
                    <div className="flex-grow">
                      <textarea
                        value={indicador}
                        onChange={(e) => manejarCambioIndicador(index, e.target.value)}
                        placeholder="Escriba el indicador aquí..."
                        className="w-full p-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="2"
                        required
                      />
                    </div>
                    {formData.indicadores.length > 1 && (
                      <button
                        type="button"
                        onClick={() => eliminarIndicador(index)}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors mt-2"
                        title="Eliminar indicador"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className="px-4 py-2 mr-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
                disabled={loading}
              >
                <Save className="w-4 h-4" />
                <span>{loading ? 'Guardando...' : 'Guardar'}</span>
              </button>
            </div>
          </form>
        </div>
      </Modal>
      
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default GestionAmbitosAprendizaje;